/**
 * Nexus API Configuration
 * Based on nexus-shell environment configuration
 */

import { createNetworkConfig } from '@repo/api-client'

// Single source of truth for the API host used when running
// under `localhost` / `192.168.*` dev servers. Production picks
// up the current `window.location.host` automatically via
// `getBaseUrl`. Override per environment by setting
// `VITE_API_BASE_URL` in `.env.*`. Everything else in the codebase
// must import `DEFAULT_API_HOST` — never hardcode the domain.
export const DEFAULT_API_HOST =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'oriz.ttk.tj'

// Environment-based configuration
export const getBaseUrl = () => {
  // In browser, use current host for production or fallback to env
  if (typeof window !== 'undefined') {
    const host = window.location.host?.split(':')[0]
    const isLocal = host?.includes('localhost') || host?.includes('192.168')

    if (host && !isLocal) {
      return window.location.host
    }
  }

  return DEFAULT_API_HOST
}

// ==================== Entity / Branding ====================

const ENTITY_CACHE_KEY = 'nexus_entity_info'

export interface EntityInfo {
  id: number
  name: string
  domain: string
  url: string
  logo?: string
}

/**
 * Read cached entity info from localStorage (synchronous).
 */
function getCachedEntity(): EntityInfo | null {
  try {
    const raw = localStorage.getItem(ENTITY_CACHE_KEY)
    return raw ? (JSON.parse(raw) as EntityInfo) : null
  } catch {
    return null
  }
}

/**
 * Fetch entity info from /v1/entity/me (no auth required).
 * Caches result in localStorage for instant access on next load.
 */
export async function fetchEntityInfo(): Promise<EntityInfo | null> {
  try {
    // Relative URL: in prod same-origin works directly,
    // in dev Vite proxy forwards /v1 → API server
    const res = await fetch('/v1/entity/me')
    if (!res.ok) return getCachedEntity()
    const data = await res.json()
    const entity: EntityInfo = {
      id: data.id,
      name: data.name,
      domain: data.domain,
      url: data.url,
      logo: data.logo,
    }
    localStorage.setItem(ENTITY_CACHE_KEY, JSON.stringify(entity))
    return entity
  } catch {
    return getCachedEntity()
  }
}

/**
 * Get the app display name.
 * Priority: cached entity name → subdomain of API base URL.
 */
export function getAppName(): string {
  const cached = getCachedEntity()
  if (cached?.name) {
    return cached.name.charAt(0).toUpperCase() + cached.name.slice(1).toLowerCase()
  }
  // Use API base URL (not window.location) so localhost still resolves to "Oriz"
  const base = getBaseUrl().split(':')[0] || ''
  const subdomain = base.split('.')[0] || 'nexus'
  return subdomain.charAt(0).toUpperCase() + subdomain.slice(1).toLowerCase()
}

/**
 * Get cached entity info (synchronous, for logo etc.)
 */
export function getEntityInfo(): EntityInfo | null {
  return getCachedEntity()
}

/**
 * Deep-replace all occurrences of "Nexus" (case-sensitive) with the
 * app name inside a translation messages object.
 */
export function brandMessages<T>(messages: T, appName: string): T {
  if (appName === 'Nexus') return messages
  return JSON.parse(
    JSON.stringify(messages).replaceAll('Nexus', appName)
  ) as T
}

export const nexusConfig = {
  baseUrl: getBaseUrl(),
  apiKey: import.meta.env.VITE_API_KEY || '0186a3f2-5ff0-028c-0000-000000000000',
  apiVersion: import.meta.env.VITE_API_VERSION || '2.0.0',
  platform: 'web' as const,
  language: 'ru',
}

export function createNexusConfig() {
  return createNetworkConfig({
    baseUrl: nexusConfig.baseUrl,
    apiKey: nexusConfig.apiKey,
    apiVersion: nexusConfig.apiVersion,
    platform: nexusConfig.platform,
    language: nexusConfig.language,
  })
}
